import React from 'react';
import styled from '@emotion/styled';
import { font } from 'src/shared/styles/variables';
import { colors } from '@modernhealth/vital-foundation';
import { ColorScheme } from 'src/generated/webapp_gql';

const applyColorPalette = (variant?: ColorScheme | null) => {
    if (!!variant) {
        const colorName = variant.toLowerCase();
        return {
            backgroundColor: colors[`${colorName}05`],
            borderColor: colors[`${colorName}05`],
            color: colors[`${colorName}85`],
            '&:focus, &:focus-visible': {
                borderColor: colors[`${colorName}55`],
                outline: 'none',
            },
            '&:hover': {
                backgroundColor: colors[`${colorName}10`],
                borderColor: colors[`${colorName}55`],
                outline: 'none',
            },
        };
    } else {
        return {
            backgroundColor: colors.white,
            borderColor: colors.blue10,
            color: colors.blue85,
            '&:focus, &:focus-visible': {
                backgroundColor: colors.white,
                borderColor: colors.blue55,
                outline: 'none',
            },
            '&:hover': {
                backgroundColor: colors.blue05,
                borderColor: colors.blue55,
                outline: 'none',
            },
        };
    }
};

const applyIconColorFilter = (variant?: ColorScheme | null) => {
    switch (variant) {
        case ColorScheme.Blue:
            return 'brightness(12%) saturate(56%) invert(26%) sepia(90%) saturate(1664%) hue-rotate(189deg) brightness(96%) contrast(102%)';
        case ColorScheme.Cyan:
            return 'brightness(0) saturate(100%) invert(23%) sepia(64%) saturate(3363%) hue-rotate(168deg) brightness(91%) contrast(101%)';
        case ColorScheme.Orange:
            return 'brightness(0) saturate(92%) invert(30%) sepia(98%) saturate(682%) hue-rotate(364deg) brightness(105%) contrast(107%)';
        case ColorScheme.Red:
            return 'brightness(0) saturate(100%) invert(53%) sepia(36%) saturate(1220%) hue-rotate(299deg) brightness(72%) contrast(90%)';
        case ColorScheme.Violet:
            return 'brightness(0) saturate(100%) invert(28%) sepia(76%) saturate(3617%) hue-rotate(246deg) brightness(96%) contrast(100%)';
        default:
            return 'brightness(0) saturate(100%) invert(44%) sepia(91%) saturate(1432%) hue-rotate(194deg) brightness(99%) contrast(111%)';
    }
};

const StyledTopic = styled('div')(({ colorScheme }: TopicTileProps) => ({
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'space-between',
    alignItems: 'space-between',
    borderRadius: 6,
    borderWidth: 2,
    borderStyle: 'solid',
    cursor: 'pointer',
    width: '100%',
    padding: '1.5rem',
    height: 218,
    transition: '0.2s background-color, 0.2s border-color',
    ...font.medium(font.size.large),
    ...applyColorPalette(colorScheme),
}));

const TopicTitle = styled.p({
    overflow: 'hidden',
    display: '-webkit-box',
    textOverflow: 'ellipsis',
    maxHeight: '100%',
    marginRight: 'auto',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
});

const TopicIcon = styled('img')(({ colorScheme }: TopicTileProps) => ({
    height: 80,
    marginLeft: 'auto',
    filter: applyIconColorFilter(colorScheme),
}));

interface TopicTileProps {
    colorScheme?: ColorScheme | null;
}
interface Props {
    title: string;
    image?: string | null;
    colorScheme?: ColorScheme | null;
    testId?: string;
    onClick: () => void;
}

const Topic = ({ title, testId, image, colorScheme, onClick }: Props) => (
    <StyledTopic data-testid={testId} onClick={onClick} colorScheme={colorScheme} tabIndex={0}>
        <TopicTitle>{title}</TopicTitle>
        {image && <TopicIcon src={image} colorScheme={colorScheme} />}
    </StyledTopic>
);

export default Topic;
